import React from 'react';
import { Box, Divider, Grid, InputLabel, Modal, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useField } from 'formik';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { UploadCloud } from 'lucide-react';
import { Primary, Secondary } from 'ui-components/Buttons';
import CustomValidationText from 'ui-components/CustomValidationText';
import { ReactComponent as UploadDocumentsIcon } from 'assets/rounds_icons/upload_documents_icon.svg';
import { BORDER, FieldIcon, INDIGO, MUTED, PLACEHOLDER } from '../primitives';

// Figma's dashed drop zone on the outside; the app's existing crop-and-upload
// behaviour underneath. The cropper flow (react-cropper, 1:1 aspect,
// getCroppedCanvas -> Blob -> File -> handleUpdateFile) is the same one
// ui-components/formik/CustomLogoButton runs for the old UploadPhoto /
// UploadOrgPhoto steps, so the file that reaches SaveProfileImage is identical.
// It lives here rather than in CustomLogoButton so the shared component — used
// by other pages — keeps its current look.

const blobToFile = (blob, fileName) => new File([blob], fileName, { type: blob.type });

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#FFFFFF',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  borderRadius: '24px',
  p: 5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '90vw',
  maxWidth: '520px',
  maxHeight: '80dvh',
  overflow: 'auto',
};

const ImageDropzone = ({
  name,
  value,
  handleUpdateFile,
  title = 'Crop Logo',
  // Compact mode (partner photos): a small square, solid border, no caption, and a
  // gradient outline icon. Every default below keeps Step 1's zone byte-identical.
  minHeight = 260,
  previewSize = 120,
  showHint = true,
  dashed = true,
  icon = UploadCloud,
  emptyIconSize = 34,
  error,
  placeholder,
  // Step 1's zone fills its grid column and stretches to the row; a partner photo is
  // a fixed square. Defaults reproduce the Step 1 behaviour exactly.
  width = '100%',
  height = '100%',
  // The Update / Remove row under the preview. It does not fit a fixed square.
  showActions = true,
}) => {
  const [, meta] = useField(name);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(value || null);
  const [previousImage, setPreviousImage] = React.useState(value || null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const cropperRef = React.useRef(null);
  const inputId = `${name}-dropzone`;

  // Same preview resolution as CustomLogoButton: File -> data URL, string -> URL.
  React.useEffect(() => {
    if (value && (value instanceof Blob || value instanceof File)) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(value);
    } else if (typeof value === 'string') {
      setImagePreview(value);
    } else {
      setImagePreview(null);
    }
  }, [value]);

  const handleFileChange = (file) => {
    setPreviousImage(imagePreview);
    if (file?.type?.includes('image')) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsModalOpen(true);
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileChange(file);
      event.target.value = null; // allow re-selecting the same file
    }
  };

  const handleCropButton = async (event) => {
    event.preventDefault();
    try {
      const cropper = cropperRef.current.cropper;
      const croppedCanvas = cropper.getCroppedCanvas();
      if (croppedCanvas) {
        croppedCanvas.toBlob((blob) => {
          const fileName = selectedFile ? selectedFile.name : 'cropped-image.png';
          const croppedFile = blobToFile(blob, fileName);
          handleUpdateFile(croppedFile);
          setIsModalOpen(false);
          setImagePreview(URL.createObjectURL(croppedFile));
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancelButton = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setImagePreview(previousImage);
  };

  const previewSrc =
    typeof imagePreview === 'string' &&
    !imagePreview.includes('data:image') &&
    !imagePreview.includes('blob:')
      // Use `&` when the stored URL already carries a query string (e.g. a signed
      // S3/GCS url) — a second `?` corrupts the signature and the image 403s.
      ? `${imagePreview}${imagePreview.includes('?') ? '&' : '?'}cacheBuster=${new Date().getTime()}`
      : imagePreview;

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFileChange(file);
      }}
    >
      <input
        accept=".png, .jpg, .jpeg"
        style={{ display: 'none' }}
        id={inputId}
        type="file"
        onChange={handleFileInputChange}
      />

      <Box
        sx={{
          minHeight,
          width,
          height,
          borderRadius: '12px',
          border: dashed
            ? `1.5px dashed ${dragging ? INDIGO : '#B9BDF7'}`
            : `1px solid ${error ? '#F04438' : dragging ? INDIGO : BORDER}`,
          background: dragging ? 'rgba(99,102,241,0.04)' : 'transparent',
          transition: 'border-color .15s ease, background .15s ease',
          display: 'grid',
          placeItems: 'center',
          p: showHint ? 3 : 1.5,
          // A fixed-size box must clip its contents rather than spill over its neighbours.
          overflow: 'hidden',
        }}
      >
        {imagePreview && !showActions ? (
          // Compact (partner photo): the box is a fixed square, so the preview fills it
          // and the Update/Remove row is dropped — together they overflow the square and
          // spill over the fields beside it. Clicking the photo re-opens the cropper.
          <InputLabel
            htmlFor={inputId}
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={previewSrc}
              alt="Uploaded Preview"
              loading="eager"
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </InputLabel>
        ) : imagePreview ? (
          <Box sx={{ display: 'grid', placeItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={previewSrc}
              alt="Uploaded Preview"
              loading="eager"
              sx={{
                width: previewSize,
                height: previewSize,
                borderRadius: '16px',
                objectFit: 'cover',
                border: '1px solid rgb(102, 112, 133, 0.5)',
              }}
            />
            <Grid
              container
              display="flex"
              alignItems="center"
              flexWrap="nowrap"
              justifyContent="center"
              gap={1}
              mt="1rem"
            >
              <Grid item sx={{ maxWidth: 'max-content' }}>
                <InputLabel
                  htmlFor={inputId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  <UploadDocumentsIcon
                    style={{ width: 16, height: 16, stroke: 'rgb(105, 65, 198)' }}
                  />
                  <span style={{ color: '#6941C6' }}>Update</span>
                </InputLabel>
              </Grid>
              <Divider orientation="vertical" flexItem />
              <Grid item sx={{ maxWidth: 'max-content' }}>
                <InputLabel
                  onClick={() => handleUpdateFile(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  <Close sx={{ width: 16, height: 16, color: 'rgb(239, 83, 80)', margin: 0 }} />
                  <span style={{ color: 'rgb(239, 83, 80)' }}>Remove</span>
                </InputLabel>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <InputLabel
            htmlFor={inputId}
            sx={{
              display: 'grid',
              placeItems: 'center',
              gap: 1,
              textAlign: 'center',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {placeholder ? (
              <Box
                component="img"
                src={placeholder}
                alt="Add"
                sx={{ width: previewSize * 0.6, height: previewSize * 0.6, objectFit: 'contain' }}
              />
            ) : (
              <FieldIcon icon={icon} size={emptyIconSize} />
            )}
            {showHint && (
              <>
                <Typography sx={{ fontSize: '0.95rem', color: MUTED }}>
                  <Box component="span" sx={{ color: INDIGO, fontWeight: 600 }}>
                    Click to upload
                  </Box>{' '}
                  or drag and drop
                </Typography>
                {/* Figma says "Max. 2MB", but the app accepts up to 10MB (see the image
                    validation in the step schema). The caption states the real limit. */}
                <Typography sx={{ fontSize: '0.85rem', color: PLACEHOLDER }}>
                  PNG, JPG or JPEG (Max. 10MB)
                </Typography>
              </>
            )}
          </InputLabel>
        )}
      </Box>

      {meta && meta.error && <CustomValidationText value={meta.error} />}

      {/* Same crop modal as CustomLogoButton */}
      <Modal open={isModalOpen} onClose={() => {}}>
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 3,
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#101828',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ width: '100%', height: '350px', borderRadius: '12px', overflow: 'hidden' }}>
            <Cropper
              src={imagePreview}
              style={{ width: '100%', height: '100%' }}
              aspectRatio={1}
              guides
              width={500}
              height={200}
              ref={cropperRef}
              viewMode={2}
            />
          </Box>
          <Box
            sx={{ display: 'flex', gap: '16px', marginTop: '24px', justifyContent: 'center', width: '100%' }}
          >
            <Secondary type="button" onClick={handleCancelButton} sx={{ py: 1.8 }} fullWidth>
              Back
            </Secondary>
            <Primary type="button" onClick={(e) => handleCropButton(e)} sx={{ py: 1.8 }} fullWidth>
              Select
            </Primary>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ImageDropzone;
